import { useState, useRef, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Icon, WaitingInline } from '~/components/ui';
import { formatNumber, useT } from '~/i18n';
import CVScoreCard from './components/CVScoreCard';
import { fetchCVScore } from './api/cvScoreClient';
import { fetchProfile } from './api/profileClient';
import { SavingLine } from './components/ProfilePieceCard';
import { prepareCvFile, uploadCvFile } from './api/cvUploadClient';
import { normalizeScoreDetail, type CVScoreDetail } from './lib/cvScore';

interface UploadResult {
  _id: string;
  skills: string[];
  parsed_length: number;
  original_url: string;
  cv_score?: number | null;
  cv_score_detail?: unknown;
  /** El perfil por piezas se está armando con este archivo, suelto en el servidor (R16). */
  profile_pending?: boolean;
}

/** Cada cuánto se pregunta por el perfil después de subir, y hasta cuándo. */
const PROFILE_POLL_MS = 4000;
const PROFILE_POLL_MAX_MS = 2 * 60 * 1000;

/** Lo que falta se contesta en «Mejorar mi hoja de vida», una carencia a la vez. */
const IMPROVE_HREF = '/perfil/hoja-de-vida/mejorar';

/**
 * Cómo va la nota de esta pantalla. `pending` es lo primero que se ve porque la
 * primera lectura de un CV viejo SÍ llama al modelo (2-4 s): es la única vez, y
 * después queda guardada.
 */
type ScoreState =
  | { kind: 'loading' }
  /** Sin hoja de vida todavía: no hay nada que calificar, y eso no es un error. */
  | { kind: 'none' }
  /**
   * `cvId`: la hoja de vida calificada (la primaria). "Agregar estos datos" va
   * a Mejorar mi hoja de vida. "Repasar mis respuestas" lleva al cuestionario
   * solo SIN perfil por piezas (`hasProfile`): con perfil, reenviar el
   * cuestionario precargado no es la forma de corregir, y se ofrece Mejorar.
   */
  | { kind: 'ready'; detail: CVScoreDetail; cvId?: string; hasProfile?: boolean }
  /** Sin `message`: el modelo no alcanzó; el texto lo pone la pantalla en el idioma activo. */
  | { kind: 'retry'; message?: string };

/** Volver al cuestionario para repasar ESTA hoja de vida (la reemplaza al terminar). */
function screeningHref(cvId: string | undefined): string {
  return cvId ? `/onboarding/screening?replaces=${encodeURIComponent(cvId)}` : '/onboarding/screening';
}

export default function JobApplyCVPage() {
  const t = useT('onboarding');
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<UploadResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [score, setScore] = useState<ScoreState>({ kind: 'loading' });
  /** El perfil se arma con el archivo recién subido: «Estamos actualizando tu hoja de vida». */
  const [syncing, setSyncing] = useState(false);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Sube con cada espera nueva: una vuelta vieja que vuelve de la red se descarta. */
  const pollGen = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const tImprove = useT('onboarding').improveCV;

  useEffect(
    () => () => {
      if (pollRef.current) clearTimeout(pollRef.current);
      pollRef.current = null;
      pollGen.current += 1;
    },
    [],
  );

  /**
   * Después de subir, el perfil por piezas se arma suelto en el servidor (un
   * minuto o más: decompose + render + grade). Se pregunta cada 4 s, una
   * pregunta a la vez, hasta 2 min. Mientras tanto queda a la vista la nota del
   * archivo; al terminar, la del perfil, que es con la que se postula. Si no
   * termina a tiempo, no pasa nada: la próxima visita la lee.
   */
  const waitForProfile = useCallback(() => {
    if (pollRef.current) clearTimeout(pollRef.current);
    const gen = ++pollGen.current;
    setSyncing(true);
    const started = Date.now();
    const tick = async () => {
      const state = await fetchProfile();
      // Se desmontó, o una subida nueva empezó su propia espera.
      if (gen !== pollGen.current) return;
      if (state.status === 'ok' && state.view.pending_inputs === 0) {
        const detail = normalizeScoreDetail(state.view.score);
        if (detail) {
          pollRef.current = null;
          setSyncing(false);
          setScore({ kind: 'ready', detail, cvId: state.view.cv_id ?? undefined, hasProfile: true });
          return;
        }
      }
      if (Date.now() - started >= PROFILE_POLL_MAX_MS) {
        pollRef.current = null;
        setSyncing(false);
        return;
      }
      pollRef.current = setTimeout(() => void tick(), PROFILE_POLL_MS);
    };
    pollRef.current = setTimeout(() => void tick(), PROFILE_POLL_MS);
  }, []);

  /**
   * La nota se pide SIEMPRE al entrar, no sólo después de subir un archivo.
   *
   * Esta es la pantalla a la que vuelve alguien que ya terminó el alta: si el
   * puntaje sólo apareciera al subir, el coaching se acabaría el día del
   * onboarding, que es justo cuando todavía no importa. Acá importa.
   */
  const loadScore = useCallback(async () => {
    setScore({ kind: 'loading' });
    const state = await fetchCVScore();
    if (state.status === 'ok') {
      return setScore({ kind: 'ready', detail: state.detail, cvId: state.cvId || undefined, hasProfile: state.hasProfile === true });
    }
    if (state.status === 'no_cv') return setScore({ kind: 'none' });
    if (state.status === 'unavailable') {
      return setScore({ kind: 'retry' });
    }
    setScore({ kind: 'retry', message: state.message });
  }, []);

  useEffect(() => {
    loadScore();
  }, [loadScore]);

  // Se valida y se lee a memoria al elegirlo (api/cvUploadClient.ts): si el
  // teléfono no deja abrir el archivo, se sabe acá y no al tocar «Subir».
  const handleFileSelect = useCallback(async (f: File) => {
    setError(null);
    setResult(null);
    try {
      setFile(await prepareCvFile(f));
    } catch (err) {
      setFile(null);
      setError(err instanceof Error ? err.message : t.upload.failed);
    }
  }, [t]);

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const onDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const dropped = e.dataTransfer.files[0];
      if (dropped) handleFileSelect(dropped);
    },
    [handleFileSelect],
  );

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selected = e.target.files?.[0];
      if (selected) handleFileSelect(selected);
    },
    [handleFileSelect],
  );

  const upload = useCallback(async () => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const data = await uploadCvFile<UploadResult>(file);
      setResult(data);
      setFile(null);
      // La subida ya trae la nota del documento nuevo. Si el modelo se cayó
      // viene en null —el CV se guardó igual— y ahí sí se vuelve a preguntar.
      const fresh = normalizeScoreDetail(data.cv_score_detail);
      if (fresh) {
        // La subida arma (o alimenta) el perfil por piezas: desde ya, se mejora en Mis datos.
        setScore((prev) => ({
          kind: 'ready',
          detail: fresh,
          cvId: data._id ? String(data._id) : undefined,
          hasProfile: data.profile_pending === true || (prev.kind === 'ready' && prev.hasProfile === true),
        }));
      } else loadScore();
      if (data.profile_pending) waitForProfile();
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : t.upload.failed);
    } finally {
      setUploading(false);
    }
  }, [file, loadScore, t, waitForProfile]);

  return (
    <div className="space-y-4">
      {/* ── La nota, antes que el cargador ────────────────────────────────────
          Quien vuelve a esta pantalla ya subió algo: lo que necesita saber no es
          cómo subir otro archivo sino qué tiene flojo el que ya está. El
          cargador sigue abajo, para quien de verdad viene a reemplazarla. */}
      {score.kind === 'loading' && <WaitingInline label={t.cvPage.scoring} />}

      {syncing && <SavingLine label={tImprove.saving} />}

      {score.kind === 'retry' && (
        <div className="card">
          <div className="card-body py-4 flex flex-row items-center gap-3 flex-wrap">
            <p className="text-sm hush flex-1 min-w-[12rem]">{score.message ?? t.cvPage.scoreUnavailable}</p>
            <button type="button" className="btn btn-sm btn-outline gap-2" onClick={loadScore}>
              <Icon name="refresh" size={14} />
              {t.cvPage.retry}
            </button>
          </div>
        </div>
      )}

      {score.kind === 'ready' && (
        <CVScoreCard
          detail={score.detail}
          scope={t.cvPage.scope}
          onAddGap={() => navigate(IMPROVE_HREF)}
          action={
            <>
              <Link
                to={score.detail.missing.length > 0 || score.hasProfile ? IMPROVE_HREF : screeningHref(score.cvId)}
                className="btn btn-primary btn-sm"
              >
                {score.detail.missing.length > 0
                  ? t.cvPage.addMissing
                  : score.hasProfile
                    ? tImprove.title
                    : t.cvPage.reviewAnswers}
              </Link>
              <span className="text-xs hush">{t.cvPage.orUploadBelow}</span>
            </>
          }
        />
      )}

      <div className="card">
        <div className="card-body">
          <h2 className="card-title">
            {score.kind === 'none' ? t.upload.title : t.cvPage.uploadNew}
          </h2>
          <p className="hush">{t.upload.ledeStandalone}</p>

          <input
            ref={inputRef}
            type="file"
            accept=".pdf,.docx"
            className="hidden"
            onChange={onInputChange}
          />

          <div
            role="button"
            tabIndex={0}
            className="drop"
            data-over={dragOver ? 'true' : undefined}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
            }}
          >
            <div className="flex flex-col items-center gap-2">
              <span className="drop__art">
                <Icon name="doc" size={26} />
              </span>
              {file ? (
                <>
                  <p className="text-lg font-medium">{file.name}</p>
                  <p className="text-sm hush">{(file.size / 1024).toFixed(0)} KB</p>
                </>
              ) : (
                <>
                  <p className="text-lg font-medium">{t.upload.dropCv}</p>
                  <p className="text-sm hush">{t.upload.dropCvHint}</p>
                </>
              )}
            </div>
          </div>

          {error && (
            <div className="alert alert-error text-sm mt-4">{error}</div>
          )}

          {file && !result && (
            <div className="card-actions justify-end mt-4">
              <button
                className="btn btn-primary"
                onClick={upload}
                disabled={uploading}
              >
                {uploading && (
                  <span className="loading loading-spinner loading-sm" />
                )}
                {t.upload.submit}
              </button>
            </div>
          )}

          {result && (
            <div className="mt-6 space-y-4">
              <div className="alert alert-success">
                <Icon name="check" />
                {t.upload.processed}
              </div>

              <div>
                <h3 className="font-semibold mb-2">{t.upload.skillsTitle}</h3>
                <div className="flex flex-wrap gap-2">
                  {result.skills.length > 0 ? (
                    result.skills.map((skill) => (
                      <span key={skill} className="chip">
                        {skill}
                      </span>
                    ))
                  ) : (
                    <p className="hush text-sm">{t.upload.noSkills}</p>
                  )}
                </div>
              </div>

              <p className="text-sm hush">
                {t.upload.extracted(formatNumber(result.parsed_length))}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
